import { AloftPrice } from '@aloft/shared-objects';
import { formatRevisionType, schAloftPrice } from '@aloft/toolkit-shared';
import { Block, Flex, SimpleInput, Typography } from '@aloft/toolkit-sui';

import { useEffect } from 'react';
import { Controller, Form, FormProvider, SubmitHandler } from 'react-hook-form';

import { FormTextarea } from '../../components/FormTextarea/FormTextarea.tsx';
import { useTkv2ReferenceBarEvents } from '../../components/ReferenceBar/useTkv2ReferenceBarEvents.ts';
import { useAppraisifyValidation } from '../../hooks/validation/useAppraisifyValidation.ts';
import { submitReconciliation } from '../../redux/features/reconciliationSlice.ts';
import {
  setReferenceViewOpen,
  setSelectedDataSheet,
} from '../../redux/features/referenceBarUISlice.ts';
import { selectCommentary } from '../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectReconciliation } from '../../redux/features/selectors/selectReconciliation.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { ReconciliationSummarizedValues } from './components/ReconciliationSummarizedValues.tsx';
import { useReconciliationForm } from './hooks/useReconciliationForm.ts';
import { ReconciliationFormOutput } from './schemas.ts';
import { selectOrder } from '../../redux/features/selectors/orderSelectors.ts';

export const ReconciliationModule = () => {
  const dispatch = useAppDispatch();
  const form = useReconciliationForm();
  const { reconciliation: reconciliationCommentary } = useAppSelector((state) =>
    selectCommentary(state),
  );
  const reconciliation = useAppSelector((state) => selectReconciliation(state));
  const order = useAppSelector((state) => selectOrder(state));
  const { isRevision, revisionData } = order;
  const revisionCommentaryLabel = `${formatRevisionType(revisionData?.revisionType || 'rov')} Commentary`;
  // Only reset form on initial load, not on every state change
  useEffect(() => {
    form.reset({
      reconciliationCommentary: reconciliationCommentary?.reconciliationCommentary ?? '',
      highestAndBestUseCommentary: reconciliationCommentary?.highestAndBestUseCommentary ?? '',
      totalCostToCure: reconciliation.totalCostToCure
        ? schAloftPrice.parse(reconciliation.totalCostToCure)
        : undefined,
      opinionOfMarketValue: reconciliation.opinionOfMarketValue
        ? schAloftPrice.parse(reconciliation.opinionOfMarketValue)
        : undefined,
      revisionCommentary: reconciliationCommentary?.revisionCommentary ?? '',
      alternativeComparablesCommentary: reconciliationCommentary?.alternativeComparablesCommentary ?? [],
    });
  }, []); // Empty dependency array - only run on mount

  const validation = useAppraisifyValidation();
  const {
    totalCostToCure: totalCostToCureError,
    opinionOfMarketValue: opinionOfMarketValueError,
    highestAndBestUseCommentary: highestAndBestUseCommentaryError,
    reconciliationCommentary: reconciliationCommentaryError,
    revisionCommentary: revisionCommentaryError,
    alternativeComparablesCommentary: alternativeComparablesCommentaryError,
  } = validation.errors.Reconciliation;
  useEffect(() => {
    dispatch(setSelectedDataSheet('pdf'));
    dispatch(setReferenceViewOpen(true));
  }, []);

  const onSubmit: SubmitHandler<ReconciliationFormOutput> = (data) => {
    dispatch(submitReconciliation(data));
  };

  const handleChange = () => {
    form
      .handleSubmit(onSubmit, undefined)()
      .catch(() => { });
  };

  return (
    <Flex {...useTkv2ReferenceBarEvents()} width="100%">
      <FormProvider {...form}>
        <Form style={{ width: '100%' }} onChange={handleChange}>
          <Flex
            margin="15px"
            flexDirection="column"
            gap={4}
            flex={1}
            width="95%"
            minWidth="600px"
            maxWidth="900px"
            paddingBottom="50px"
          >
            <Typography variant="h1">Reconciliation</Typography>
            {reconciliation?.includeTotalCostToCure && (
              <Flex flex={1} justifyContent="space-between" alignItems="center">
                <Block>
                  <Typography variant="h3" color="textSecondary">
                    Total Cost to Cure
                    <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                      *
                    </span>
                  </Typography>
                </Block>
                <Controller
                  name="totalCostToCure"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <SimpleInput
                      type="number"
                      value={field.value ? field.value.toString(true, true) : ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value
                          ? AloftPrice.fromDollarLike(e.target.value)
                          : undefined;
                        field.onChange(value);
                        handleChange();
                      }}
                      onBlur={field.onBlur}
                      precision={0}
                      prefix="$ "
                      useSeparator
                      required
                      placeholder="Required"
                      width="300px"
                      errorMessage={
                        fieldState.isTouched ? totalCostToCureError?.message : undefined
                      }
                      invalid={fieldState.invalid && fieldState.isTouched}
                    />
                  )}
                />
              </Flex>
            )}
            <Flex flex={1} justifyContent="space-between" alignItems="center">
              <Block>
                <Typography variant="h3" color="textSecondary">
                  Opinion of Market Value
                  <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                    *
                  </span>
                </Typography>
              </Block>
              <Controller
                name="opinionOfMarketValue"
                control={form.control}
                render={({ field, fieldState }) => (
                  <SimpleInput
                    type="number"
                    value={field.value ? field.value.toString(true, true) : ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value
                        ? AloftPrice.fromDollarLike(e.target.value)
                        : undefined;
                      field.onChange(value);
                      handleChange();
                    }}
                    onBlur={field.onBlur}
                    precision={0}
                    prefix="$ "
                    useSeparator
                    required
                    placeholder="Required"
                    width="300px"
                    errorMessage={
                      fieldState.isTouched ? opinionOfMarketValueError?.message : undefined
                    }
                    invalid={fieldState.invalid && fieldState.isTouched}
                  />
                )}
              />
            </Flex>
            <ReconciliationSummarizedValues />
            {isRevision && (
              <>
                <Flex flex={1} flexDirection="column">
                  <Controller
                    name="revisionCommentary"
                    control={form.control}
                    render={({ fieldState }) => (
                      <FormTextarea
                        name="revisionCommentary"
                        control={form.control}
                        label={revisionCommentaryLabel}
                        placeholder="Required"
                        isRequired
                        labelWidth="250px"
                        inputWidth="500px"
                        height='200px'
                        labelColor="textSecondary"
                        labelVariant="h3"
                        size="sm"
                        resize="vertical"
                        customErrorMessage={
                          fieldState.isTouched ? revisionCommentaryError?.message : undefined
                        }
                        column
                      />
                    )}
                  />
                </Flex>
                {revisionData?.comparables?.map((comp, index) => (
                  <Flex flex={1} flexDirection="column" key={comp.address}>
                    <Controller
                      name={`alternativeComparablesCommentary.${index}` as const}
                      control={form.control}
                      render={({ fieldState }) => (
                        <FormTextarea
                          name={`alternativeComparablesCommentary.${index}` as const}
                          control={form.control}
                          label={`Alternative Comparable ${index + 1}: ${comp.address}`}
                          placeholder="Required"
                          isRequired
                          labelWidth="250px"
                          inputWidth="500px"
                          labelColor="textSecondary"
                          labelVariant="h3"
                          size="sm"
                          resize="vertical"
                          column
                          customErrorMessage={
                            (fieldState.isTouched && reconciliationCommentary?.alternativeComparablesCommentary?.[index] === '')
                              ? alternativeComparablesCommentaryError?.message
                              : undefined
                          }
                        />
                      )}
                    />
                  </Flex>
                ))}
              </>
            )}
            <Flex flex={1} flexDirection="column">
              <Controller
                name="reconciliationCommentary"
                control={form.control}
                render={({ fieldState }) => (
                  <FormTextarea
                    name="reconciliationCommentary"
                    control={form.control}
                    label="Reconciliation Commentary"
                    placeholder="Required"
                    isRequired
                    labelWidth="250px"
                    inputWidth="500px"
                    labelColor="textSecondary"
                    labelVariant="h3"
                    size="sm"
                    resize="vertical"
                    height='300px'
                    customErrorMessage={
                      fieldState.isTouched ? reconciliationCommentaryError?.message : undefined
                    }
                    column
                  />
                )}
              />
            </Flex>
            <Flex flex={1} flexDirection="column">
              <Controller
                name="highestAndBestUseCommentary"
                control={form.control}
                render={({ fieldState }) => (
                  <FormTextarea
                    name="highestAndBestUseCommentary"
                    control={form.control}
                    label="Highest & Best Use Commentary"
                    placeholder="Required"
                    isRequired
                    height='300px'
                    labelWidth="250px"
                    inputWidth="500px"
                    labelColor="textSecondary"
                    labelVariant="h3"
                    size="sm"
                    resize="vertical"
                    customErrorMessage={
                      fieldState.isTouched ? highestAndBestUseCommentaryError?.message : undefined
                    }
                    column
                  />
                )}
              />
            </Flex>
          </Flex>
        </Form>
      </FormProvider>
    </Flex>
  );
};
